import { useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const capitalBands = ["Below ₹50L", "₹50L – ₹1 Cr", "₹1 Cr – ₹2 Cr", "₹2 Cr – ₹5 Cr", "₹5 Cr+"];
const objectives = ["Rental income", "Own-use", "Commercial", "Development", "Not sure"];

export const LeadForm = ({ idPrefix = "lead", compact = false }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    owns_land: "",
    plot_size: "",
    location: "",
    capital_band: capitalBands[0],
    objective: objectives[0],
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("sent");
    } catch {
      setStatus("idle");
      setError("Could not send. Please try again or call us directly.");
    }
  };

  if (status === "sent") {
    return (
      <div
        className={`flex flex-col justify-center rounded-xl border-2 border-amber bg-cream ${compact ? "p-8" : "min-h-[280px] p-10"}`}
        data-testid={`${idPrefix}-success-message`}
      >
        <p className="font-display text-3xl font-extrabold tracking-tight text-navy">Received.</p>
        <p className="mt-4 max-w-md text-base leading-relaxed text-gray-600">
          Your details are in our system. Expect a call from Urban Veera
          shortly — usually the same day.
        </p>
      </div>
    );
  }

  const gap = compact ? "space-y-6" : "space-y-7";
  const gridGap = compact ? "gap-6" : "gap-7";

  return (
    <form onSubmit={submit} className={gap} data-testid={`${idPrefix}-form`}>
      <div className={`grid grid-cols-1 ${gridGap} md:grid-cols-2`}>
        <div>
          <label htmlFor={`${idPrefix}-name`} className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Your name</label>
          <input id={`${idPrefix}-name`} data-testid={`${idPrefix}-name-input`} required value={form.name} onChange={update("name")} placeholder="Full name" className="field-input mt-2" />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-phone`} className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Phone</label>
          <input id={`${idPrefix}-phone`} data-testid={`${idPrefix}-phone-input`} required type="tel" value={form.phone} onChange={update("phone")} placeholder="+91 ..." className="field-input mt-2" />
        </div>
      </div>

      <div className={`grid grid-cols-1 ${gridGap} md:grid-cols-2`}>
        <div>
          <label htmlFor={`${idPrefix}-city`} className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">City</label>
          <input id={`${idPrefix}-city`} data-testid={`${idPrefix}-city-input`} required={!compact} value={form.city} onChange={update("city")} placeholder="Bengaluru" className="field-input mt-2" />
        </div>
        {compact ? (
          <div>
            <label htmlFor={`${idPrefix}-email`} className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">
              Email <span className="normal-case tracking-normal text-gray-400">(optional)</span>
            </label>
            <input id={`${idPrefix}-email`} data-testid={`${idPrefix}-email-input`} type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" className="field-input mt-2" />
          </div>
        ) : (
          <div>
            <label htmlFor={`${idPrefix}-owns-land`} className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Do you own land?</label>
            <select id={`${idPrefix}-owns-land`} data-testid={`${idPrefix}-owns-land-select`} required value={form.owns_land} onChange={update("owns_land")} className="field-input mt-2">
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No — sourcing needed</option>
            </select>
          </div>
        )}
      </div>

      {!compact && (
        <>
          <div className={`grid grid-cols-1 ${gridGap} md:grid-cols-2`}>
            <div>
              <label htmlFor={`${idPrefix}-plot-size`} className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Plot size</label>
              <input id={`${idPrefix}-plot-size`} data-testid={`${idPrefix}-plot-size-input`} value={form.plot_size} onChange={update("plot_size")} placeholder="e.g. 30 × 40 ft" className="field-input mt-2" />
            </div>
            <div>
              <label htmlFor={`${idPrefix}-capital-band`} className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Approximate capital available</label>
              <select id={`${idPrefix}-capital-band`} data-testid={`${idPrefix}-capital-band-select`} value={form.capital_band} onChange={update("capital_band")} className="field-input mt-2">
                {capitalBands.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>

          <div className={`grid grid-cols-1 ${gridGap} md:grid-cols-2`}>
            <div>
              <label htmlFor={`${idPrefix}-location`} className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">
                Preferred location <span className="normal-case tracking-normal text-gray-400">(optional)</span>
              </label>
              <input id={`${idPrefix}-location`} data-testid={`${idPrefix}-location-input`} value={form.location} onChange={update("location")} placeholder="e.g. Nagavara, HSR Layout" className="field-input mt-2" />
            </div>
            <div>
              <label htmlFor={`${idPrefix}-objective`} className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">
                Project objective <span className="normal-case tracking-normal text-gray-400">(optional)</span>
              </label>
              <select id={`${idPrefix}-objective`} data-testid={`${idPrefix}-objective-select`} value={form.objective} onChange={update("objective")} className="field-input mt-2">
                {objectives.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
        </>
      )}

      <div>
        <label htmlFor={`${idPrefix}-message`} className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">
          {compact ? "Anything else? (optional)" : "Additional message (optional)"}
        </label>
        <textarea
          id={`${idPrefix}-message`}
          data-testid={`${idPrefix}-message-input`}
          rows={compact ? 2 : 2}
          value={form.message}
          onChange={update("message")}
          placeholder="Timeline, questions, anything specific..."
          className="field-input mt-2 resize-none"
        />
      </div>

      {error && (
        <p className="text-sm font-semibold text-red-600" data-testid={`${idPrefix}-error-message`}>{error}</p>
      )}

      <button
        type="submit"
        data-testid={`submit-${idPrefix}-btn`}
        disabled={status === "sending"}
        className={`rounded-md bg-amber px-8 py-4 text-sm font-bold text-navy transition-colors duration-300 hover:bg-amberdark hover:text-white disabled:opacity-50 ${compact ? "w-full" : "w-full md:w-auto"}`}
      >
        {status === "sending" ? "Sending..." : compact ? "Request a Callback" : "Request Feasibility"}
      </button>
    </form>
  );
};
